/**
 * Decide how a public-catalog CTA reaches its destination.
 *
 * On plain web these buttons open a new tab, so the gym's catalog stays put
 * behind the enrolment page. Inside the CheerVue app's webview sheet there is no
 * new tab to open, and the two platforms diverge (react-native-webview 13.13.5):
 *
 * - Android routes `target=_blank` to `onCreateWindow`, which builds a `WebView`
 *   and passes it to the transport WITHOUT ever adding it to the view hierarchy
 *   (RNCWebChromeClient.java:89-114). That is unconditional — setting
 *   `onOpenWindow` only attaches a `WebViewClient` that reports the URL back to
 *   JS, so it does not make the new view render either. The destination loads
 *   somewhere nobody can see, and to the parent the tap does nothing.
 * - iOS has a fallback: for a navigation whose target frame is not the main
 *   frame, `createWebViewWithConfiguration` calls `[webView loadRequest:]` on
 *   the existing webview when no `onOpenWindow` is set (RNCWebViewImpl.m:400),
 *   so the destination replaces the current page rather than vanishing.
 *
 * The sheet sets neither `onOpenWindow` nor `setSupportMultipleWindows={false}`,
 * and the Android default for that setting is `true` (WebView.android.tsx:76).
 *
 * So in-app we navigate in place on both, which is also what the program cards
 * on the catalog already do with a plain `<Link href>`. That is a deliberate
 * choice rather than an Android workaround: relying on the iOS fallback means
 * relying on a library's else-branch to paper over a call we did not want.
 *
 * `isMobileWebView` comes from `useIsMobileWebView`, a presentation hint rather
 * than a boundary. Getting it wrong changes only whether a tab is spawned — both
 * branches reach the same URL — so there is nothing here to abuse.
 */
export interface OpenProgramTargetOptions {
  /** Destination path, e.g. `/programs/acme/classes/123`. */
  href: string;
  /** True when the page is rendering inside the app's webview sheet. */
  isMobileWebView: boolean;
  /** Same-view navigation — Next's `router.push`. */
  push: (href: string) => void;
  /** New-tab navigation — `window.open`. */
  openWindow: (href: string, target: string) => void;
}

export function openProgramTarget({
  href,
  isMobileWebView,
  push,
  openWindow,
}: OpenProgramTargetOptions): void {
  if (isMobileWebView) {
    push(href);
    return;
  }
  openWindow(href, '_blank');
}
